<template>
  <div class="alice-shell theme-turquoise">
    <TopBar class="alice-shell__topbar" />

    <Sidebar
      class="alice-shell__sidebar"
      variant="rail"
      active-key="requests"
    />

    <main class="alice-shell__main">
      <PageHeader
        title="Feature requests"
        :count="requests.length"
        subtitle="Missing something in Alice? Add it here so we can pick it up."
      />

      <form
        class="freq-form alice-shell__toolbar"
        @submit.prevent="submitRequest"
      >
        <input
          v-model="newText"
          type="text"
          placeholder="Describe the feature you'd like…"
          maxlength="500"
        />
        <v-btn
          color="black"
          type="submit"
          :loading="submitting"
          :disabled="!newText.trim()"
          >Request</v-btn
        >
      </form>

      <div class="alice-shell__list">
        <div v-if="loading" class="freq-loading">
          <v-progress-circular indeterminate size="28" width="3" />
        </div>
        <div v-else-if="error" class="freq-empty">
          Couldn't load the feature requests.
          <v-btn variant="text" @click="fetchRequests">Retry</v-btn>
        </div>
        <div v-else-if="requests.length === 0" class="freq-empty">
          No feature requests yet. Be the first!
        </div>

        <article v-for="r in sorted" :key="r.id" class="freq-row">
          <div class="freq-row__content">
            <p class="freq-row__text">{{ r.text }}</p>
            <div class="freq-row__meta">
              <span>{{
                r.createdByName || r.createdByEmail || "Unknown"
              }}</span>
              <span aria-hidden="true">·</span>
              <span>{{ shortDate(r.createdAt) }}</span>
            </div>
          </div>

          <div class="freq-row__side">
            <select
              v-if="isMax"
              class="freq-status-select"
              :value="r.status"
              @change="setStatus(r, $event.target.value)"
            >
              <option
                v-for="opt in statusOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
            <span v-else class="freq-chip" :class="`freq-chip--${r.status}`">{{
              statusLabel(r.status)
            }}</span>
            <button
              v-if="isMax || r.createdBy === user?.uid"
              class="freq-iconbtn"
              title="Delete"
              @click="removeRequest(r)"
            >
              <Icon name="trash" :size="16" />
            </button>
          </div>
        </article>
      </div>
    </main>
  </div>
</template>

<script>
import { mapState } from "vuex";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { db } from "@/firebase";

import TopBar from "@/components/alice/TopBar.vue";
import Sidebar from "@/components/alice/Sidebar.vue";
import PageHeader from "@/components/alice/PageHeader.vue";
import Icon from "@/components/alice/Icon.vue";

const STATUS_LABELS = {
  open: "Open",
  planned: "Planned",
  in_progress: "In progress",
  done: "Done",
  declined: "Declined",
};

export default {
  name: "RequestsPage",
  components: { TopBar, Sidebar, PageHeader, Icon },
  data() {
    return {
      requests: [],
      loading: true,
      error: false,
      newText: "",
      submitting: false,
      statusOptions: Object.entries(STATUS_LABELS).map(([value, label]) => ({
        value,
        label,
      })),
    };
  },
  computed: {
    ...mapState({ user: (s) => s.user }),
    isMax() {
      return this.user?.email === "max@braingineers.com";
    },
    sorted() {
      return [...this.requests].sort(
        (a, b) => (b.createdAt || 0) - (a.createdAt || 0)
      );
    },
  },
  created() {
    // Firestore reads require auth; on a fresh load the store user can lag
    // behind route entry, so wait for it instead of fetching into a
    // permission error.
    if (this.user) {
      this.fetchRequests();
    } else {
      const unwatch = this.$watch("user", (u) => {
        if (u) {
          unwatch();
          this.fetchRequests();
        }
      });
    }
  },
  methods: {
    async fetchRequests() {
      this.loading = true;
      this.error = false;
      try {
        const snap = await getDocs(collection(db, "featureRequests"));
        this.requests = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
      } catch (e) {
        console.error("Failed to load feature requests:", e);
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    statusLabel(status) {
      return STATUS_LABELS[status] || status;
    },
    shortDate(millis) {
      if (!millis) return "";
      return new Date(millis).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    },
    async submitRequest() {
      const text = this.newText.trim();
      if (!text || !this.user?.uid) return;
      this.submitting = true;
      try {
        const data = {
          text,
          status: "open",
          createdAt: Date.now(),
          updatedAt: Date.now(),
          createdBy: this.user.uid,
          createdByName: this.user.displayName || "",
          createdByEmail: this.user.email || "",
        };
        const ref = await addDoc(collection(db, "featureRequests"), data);
        this.requests.push({ ...data, id: ref.id });
        this.newText = "";
      } finally {
        this.submitting = false;
      }
    },
    async setStatus(r, status) {
      const patch = {
        status,
        statusChangedAt: Date.now(),
        updatedAt: Date.now(),
      };
      await updateDoc(doc(db, "featureRequests", r.id), patch);
      const i = this.requests.findIndex((x) => x.id === r.id);
      if (i !== -1) this.requests[i] = { ...this.requests[i], ...patch };
    },
    async removeRequest(r) {
      if (!confirm("Delete this feature request?")) return;
      await deleteDoc(doc(db, "featureRequests", r.id));
      this.requests = this.requests.filter((x) => x.id !== r.id);
    },
  },
};
</script>

<style scoped>
.alice-shell {
  display: grid;
  grid-template-rows: 56px 1fr;
  grid-template-columns: 56px 1fr;
  height: 100vh;
  width: 100%;
  background: var(--bg-muted);
  font-family: var(--font-body);
  color: var(--fg-1);
  overflow: hidden;
}
.alice-shell__topbar {
  grid-column: 1 / -1;
  grid-row: 1;
}
.alice-shell__sidebar {
  grid-column: 1;
  grid-row: 2;
}
/* The header scrolls away with the list; only the toolbar stays in view. */
.alice-shell__main {
  grid-column: 2;
  grid-row: 2;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow-y: auto;
}
.alice-shell__main > * {
  flex-shrink: 0;
}
.alice-shell__toolbar {
  position: sticky;
  top: 0;
  z-index: 3;
}
.alice-shell__list {
  flex-grow: 1;
  background: var(--vp-white);
}

.freq-form {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 32px;
  background: var(--vp-white);
  border-bottom: 1px solid var(--border-hairline);
}
.freq-form input {
  flex: 1;
  min-width: 0;
  border: 1px solid var(--border-hairline);
  outline: none;
  background: var(--bg-muted);
  padding: 10px 14px;
  font-family: var(--font-body);
  font-size: 15px;
  color: var(--fg-1);
}
.freq-form input:focus {
  border-color: var(--vp-black);
  background: var(--vp-white);
}

.freq-empty {
  padding: 80px 40px;
  text-align: center;
  font-family: var(--font-body);
  font-size: 18px;
  color: var(--fg-2);
}
.freq-loading {
  display: flex;
  justify-content: center;
  padding: 80px 40px;
  color: var(--fg-3);
}

.freq-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 32px;
  border-bottom: 1px solid var(--border-hairline);
}
.freq-row__content {
  flex: 1;
  min-width: 0;
}
.freq-row__text {
  margin: 0;
  font-family: var(--font-body);
  font-size: 15px;
  line-height: 1.5;
  color: var(--fg-1);
}
.freq-row__meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 600;
  color: var(--fg-3);
}
.freq-row__side {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.freq-status-select {
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  padding: 4px 6px;
  border: 1px solid var(--border-hairline);
  background: var(--vp-white);
  color: var(--fg-1);
  cursor: pointer;
}

.freq-chip {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  line-height: 1;
  white-space: nowrap;
}
.freq-chip--open {
  color: var(--vp-grijs-160);
  background: var(--vp-grijs-20);
}
.freq-chip--planned {
  color: var(--fg-2);
  background: transparent;
  border: 1px solid var(--border-hairline);
}
.freq-chip--in_progress {
  color: var(--vp-geel-160);
  background: var(--vp-geel-20);
}
.freq-chip--done {
  color: var(--vp-turquoise-160);
  background: var(--vp-turquoise-20);
}
.freq-chip--declined {
  color: var(--fg-3);
  background: var(--vp-grijs-20);
}

.freq-iconbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: var(--fg-3);
  cursor: pointer;
}
.freq-iconbtn:hover {
  color: var(--vp-black);
  background: var(--bg-muted);
}
</style>
